import React, { useEffect, useState } from "react";
import { api } from "./api";

export default function StockReport({ onBack }) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);

  const [tab, setTab] = useState("onhand"); // onhand | movements

  // on hand
  const [q1, setQ1] = useState("");
  const [cat1, setCat1] = useState("");
  const [active, setActive] = useState("all");
  const [cats1, setCats1] = useState([]);
  const [onhandRows, setOnhandRows] = useState([]);
  const [onhandTotals, setOnhandTotals] = useState({ items: 0, qty_total: 0, cost_value_total: 0, sell_value_total: 0 });

  // movements
  const [from, setFrom] = useState(fmt(sevenAgo));
  const [to, setTo] = useState(fmt(today));
  const [type, setType] = useState("all");
  const [cashier, setCashier] = useState("");
  const [q2, setQ2] = useState("");
  const [types, setTypes] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [movRows, setMovRows] = useState([]);
  const [movTotals, setMovTotals] = useState({ rows_count: 0, net_change: 0, total_in: 0, total_out: 0 });

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const money = (n) => Number(n || 0).toFixed(2);
  const num = (n) => Number(n || 0).toFixed(2);

  const loadOnHand = async () => {
    try {
      setBusy(true);
      setMsg("");
      const res = await api.get("/server.php/reports/stock-on-hand", { params: { q: q1, category: cat1, active } });
      if (!res.data?.success) return setMsg(res.data?.message || "Failed to load stock on hand");
      setCats1(res.data.categories || []);
      setOnhandRows(res.data.rows || []);
      setOnhandTotals(res.data.totals || onhandTotals);
    } catch (e) {
      console.error(e);
      setMsg("Server error loading stock on hand");
    } finally {
      setBusy(false);
    }
  };

  const loadMovements = async () => {
    try {
      setBusy(true);
      setMsg("");
      const res = await api.get("/server.php/reports/stock-movements", { params: { from, to, type, cashier, q: q2 } });
      if (!res.data?.success) return setMsg(res.data?.message || "Failed to load stock movements");
      setMovRows(res.data.rows || []);
      setMovTotals(res.data.totals || movTotals);
      setTypes(res.data.types || []);
      setCashiers(res.data.cashiers || []);
    } catch (e) {
      console.error(e);
      setMsg("Server error loading stock movements");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadOnHand();
    loadMovements();
    // eslint-disable-next-line
  }, []);

  const exportCSV = (rows, header, filename) => {
    const esc = (v) => {
      const s = String(v ?? "");
      if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replaceAll('"', '""')}"`;
      return s;
    };
    const csv = header.join(",") + "\n" + rows.map(r => header.map(h => esc(r[h])).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <button style={styles.btn} onClick={onBack}>← Back</button>
        <div style={{ fontWeight: 900 }}>Stock Report</div>

        <div style={{ marginLeft: 10, display: "flex", gap: 8 }}>
          <button style={tab === "onhand" ? styles.tabOn : styles.tab} onClick={() => setTab("onhand")}>On Hand</button>
          <button style={tab === "movements" ? styles.tabOn : styles.tab} onClick={() => setTab("movements")}>Movements</button>
        </div>

        <div style={{ marginLeft: "auto", color: "#6b7280", fontSize: 12 }}>
          {busy ? "Loading..." : "Ready"}
        </div>
      </div>

      {msg ? <div style={styles.msg}>{msg}</div> : null}

      {tab === "onhand" && (
        <>
          <div style={styles.panel}>
            <div style={styles.filters}>
              <div style={{ ...styles.field, gridColumn: "span 2" }}>
                <label style={styles.label}>Search (SKU / Item)</label>
                <input style={styles.input} value={q1} onChange={(e) => setQ1(e.target.value)} placeholder="e.g. Flour" />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Category</label>
                <select style={styles.input} value={cat1} onChange={(e) => setCat1(e.target.value)}>
                  <option value="">All</option>
                  {cats1.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Active</label>
                <select style={styles.input} value={active} onChange={(e) => setActive(e.target.value)}>
                  <option value="all">All</option>
                  <option value="1">Active only</option>
                  <option value="0">Inactive only</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <button style={styles.btnPrimary} onClick={loadOnHand} disabled={busy}>Run</button>
                <button
                  style={styles.btn}
                  onClick={() =>
                    exportCSV(
                      onhandRows,
                      ["sku","item_name","category","unit","qty_on_hand","reorder_level","cost_price","sell_price","cost_value","sell_value","is_active"],
                      `stock_on_hand.csv`
                    )
                  }
                  disabled={busy || onhandRows.length === 0}
                >
                  Export CSV
                </button>
              </div>
            </div>

            <div style={styles.kpis}>
              <Kpi label="Items" value={onhandTotals.items} />
              <Kpi label="Qty Total" value={num(onhandTotals.qty_total)} />
              <Kpi label="Cost Value" value={money(onhandTotals.cost_value_total)} />
              <Kpi label="Sell Value" value={money(onhandTotals.sell_value_total)} />
            </div>
          </div>

          <div style={styles.panel}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>SKU</Th>
                    <Th>Item</Th>
                    <Th>Category</Th>
                    <ThR>On Hand</ThR>
                    <ThR>Reorder</ThR>
                    <ThR>Cost</ThR>
                    <ThR>Sell</ThR>
                    <ThR>Cost Value</ThR>
                    <ThR>Sell Value</ThR>
                  </tr>
                </thead>
                <tbody>
                  {onhandRows.map((r) => (
                    <tr key={r.id}>
                      <Td>{r.sku}</Td>
                      <Td>{r.item_name}</Td>
                      <Td>{r.category}</Td>
                      <TdR>{num(r.qty_on_hand)}</TdR>
                      <TdR>{num(r.reorder_level)}</TdR>
                      <TdR>{money(r.cost_price)}</TdR>
                      <TdR>{money(r.sell_price)}</TdR>
                      <TdR>{money(r.cost_value)}</TdR>
                      <TdR>{money(r.sell_value)}</TdR>
                    </tr>
                  ))}
                  {onhandRows.length === 0 && (
                    <tr><Td colSpan={9} style={{ textAlign: "center", color: "#6b7280", padding: 14 }}>No data</Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "movements" && (
        <>
          <div style={styles.panel}>
            <div style={styles.filters}>
              <div style={styles.field}>
                <label style={styles.label}>From</label>
                <input style={styles.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>To</label>
                <input style={styles.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Type</label>
                <select style={styles.input} value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="all">All</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Cashier</label>
                <select style={styles.input} value={cashier} onChange={(e) => setCashier(e.target.value)}>
                  <option value="">All</option>
                  {cashiers.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ ...styles.field, gridColumn: "span 2" }}>
                <label style={styles.label}>Search (SKU / Item / Note)</label>
                <input style={styles.input} value={q2} onChange={(e) => setQ2(e.target.value)} placeholder="e.g. Sale #14" />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <button style={styles.btnPrimary} onClick={loadMovements} disabled={busy}>Run</button>
                <button
                  style={styles.btn}
                  onClick={() =>
                    exportCSV(
                      movRows,
                      ["created_at","movement_type","qty_change","sku","item_name","note","cashier","inventory_id"],
                      `stock_movements_${from}_to_${to}.csv`
                    )
                  }
                  disabled={busy || movRows.length === 0}
                >
                  Export CSV
                </button>
              </div>
            </div>

            <div style={styles.kpis}>
              <Kpi label="Rows" value={movTotals.rows_count} />
              <Kpi label="Total IN" value={num(movTotals.total_in)} />
              <Kpi label="Total OUT" value={num(movTotals.total_out)} />
              <Kpi label="Net Change" value={num(movTotals.net_change)} />
            </div>
          </div>

          <div style={styles.panel}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>Created</Th>
                    <Th>Type</Th>
                    <ThR>Qty</ThR>
                    <Th>SKU</Th>
                    <Th>Item</Th>
                    <Th>Note</Th>
                    <Th>Cashier</Th>
                  </tr>
                </thead>
                <tbody>
                  {movRows.map((r) => (
                    <tr key={r.id}>
                      <Td>{r.created_at}</Td>
                      <Td>{r.movement_type}</Td>
                      <TdR style={{ fontWeight: 900 }}>{num(r.qty_change)}</TdR>
                      <Td>{r.sku}</Td>
                      <Td>{r.item_name}</Td>
                      <Td>{r.note}</Td>
                      <Td>{r.cashier}</Td>
                    </tr>
                  ))}
                  {movRows.length === 0 && (
                    <tr><Td colSpan={7} style={{ textAlign: "center", color: "#6b7280", padding: 14 }}>No data</Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div style={styles.kpi}>
      <div style={{ color: "#6b7280", fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 900, fontSize: 16 }}>{value}</div>
    </div>
  );
}
function Th({ children }) { return <th style={styles.th}>{children}</th>; }
function ThR({ children }) { return <th style={{ ...styles.th, textAlign: "right" }}>{children}</th>; }
function Td({ children, ...rest }) { return <td style={styles.td} {...rest}>{children}</td>; }
function TdR({ children, ...rest }) { return <td style={{ ...styles.td, textAlign: "right" }} {...rest}>{children}</td>; }

const styles = {
  page: { minHeight: "100vh", background: "#eef1f5", padding: 12 },
  topbar: { display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", background: "#fff", border: "1px solid #cfd6df", borderRadius: 12, marginBottom: 10 },
  panel: { background: "#fff", border: "1px solid #cfd6df", borderRadius: 12, overflow: "hidden", marginBottom: 10 },
  filters: { display: "grid", gridTemplateColumns: "repeat(6, minmax(150px, 1fr))", gap: 10, padding: 12, borderBottom: "1px solid #e7ecf2" },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 12, color: "#6b7280", fontWeight: 700 },
  input: { padding: "10px 10px", border: "1px solid #cfd6df", borderRadius: 10, fontSize: 13, outline: "none" },
  btn: { padding: "10px 12px", border: "1px solid #9aa6b2", borderRadius: 10, background: "#fff", fontWeight: 800, cursor: "pointer" },
  btnPrimary: { padding: "10px 12px", border: "1px solid #1d4ed8", borderRadius: 10, background: "#2563eb", color: "#fff", fontWeight: 900, cursor: "pointer" },
  tab: { padding: "8px 10px", border: "1px solid #cfd6df", borderRadius: 10, background: "#fff", fontWeight: 900, cursor: "pointer" },
  tabOn: { padding: "8px 10px", border: "1px solid #1d4ed8", borderRadius: 10, background: "#2563eb", color: "#fff", fontWeight: 900, cursor: "pointer" },
  msg: { padding: "8px 12px", background: "#fff", border: "1px solid #fca5a5", color: "#991b1b", fontWeight: 800, borderRadius: 12, marginBottom: 10 },
  kpis: { display: "flex", gap: 10, padding: 12, flexWrap: "wrap" },
  kpi: { minWidth: 140, padding: 10, border: "1px solid #e7ecf2", borderRadius: 12, background: "#fbfcfe" },
  tableWrap: { overflow: "auto", maxHeight: "72vh" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { position: "sticky", top: 0, background: "#eef2f7", borderBottom: "1px solid #cfd6df", textAlign: "left", padding: "8px 10px", fontSize: 12, color: "#334155", textTransform: "uppercase", letterSpacing: ".06em" },
  td: { borderBottom: "1px solid #e7ecf2", padding: "8px 10px" },
};